/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IImageStyleProps, IImageStyles, Image, ImageLoadState } from '@fluentui/react/lib/Image';
import { ILinkStyleProps, ILinkStyles, Link } from '@fluentui/react/lib/Link';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackItemProps, IStackItemSlots, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoCard } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { globalMessages } from 'src/core/utils/globalMessages';
import { learnMoreLink } from 'src/languageStudio/appShell/landingPage/utils/landingPageLinks';
import { DemoId } from 'src/languageStudio/core/utils/demosUtils';

export type CardType = 'custom' | 'prebuilt' | 'scenario';

export type PrebuiltDemoCardProps = {
    id: DemoId;
    height?: string;
    imageSrc: string;
    demoName: string;
    demoLink: string;
    subtitle?: string;
    disabled?: boolean;
    cardType: CardType;
    description: string;
    featureName: TelemetryFeatureTitle;
    tryOutMessage?: string;
    tooltipMessage?: string;
    demoType?: 'preview' | 'ga';
    isExternalLinkDemo?: boolean;
    onDemoClicked?: (demoId: DemoId) => void;
};

const messages = defineMessages({
    tryItOut: { id: 'languageStudio.prebuiltDemoCard.tryItOut', defaultMessage: 'Try it out' },
    previewTag: {
        id: 'languageStudio.prebuiltDemoCard.previewTag',
        defaultMessage: 'Preview'
    },
    gaTag: {
        id: 'languageStudio.prebuiltDemoCard.gaTag',
        defaultMessage: 'GA'
    },
    definitionTooltip: {
        id: 'languageStudio.prebuiltDemoCard.definitionTooltip',
        defaultMessage: 'This product is in { phase }.'
    }
});

const cardTheme = getScopedTheme('PrebuiltDemoCard');
const stackStylesFactory = classNamesFunction<IStackItemProps, IComponentStyles<IStackItemSlots>>();
const linkStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({
    root: { fontSize: 12, marginTop: 'auto !important', textDecoration: 'none !important' }
});
const imageStyles = classNamesFunction<IImageStyleProps, IImageStyles>()({
    image: { borderRadius: '8px 8px 0px 0px', background: cardTheme.greyCardColor }
});
const disabledContainerStyles = stackStylesFactory({
    root: { backgroundColor: cardTheme.greyCardColor, cursor: 'default', '&:hover': { boxShadow: cardTheme.boxShadow } }
});
const containerStyles = (height: string = 'auto', disabled: boolean = false) =>
    stackStylesFactory({
        root: {
            height,
            width: 260,
            minHeight: 320,
            borderRadius: 8,
            cursor: 'pointer',
            boxShadow: cardTheme.boxShadow,
            transition: 'box-shadow 100ms ease-in',
            '&:hover': { boxShadow: cardTheme.boxShadowHover, ...(!disabled ? { backgroundColor: cardTheme.hoverBackgroundColor } : {}) }
        }
    });

const tooltipLinkStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({ root: { color: `${cardTheme.linkColor} !important` } });

/**
 * Displays the general prebuilt demo card in the landing page.
 */
export const PrebuiltDemoCard = (props: PrebuiltDemoCardProps) => {
    const {
        id,
        cardType,
        imageSrc,
        disabled,
        demoName,
        demoLink,
        demoType,
        subtitle,
        description,
        featureName,
        onDemoClicked,
        tryOutMessage,
        tooltipMessage,
        height = 'auto',
        isExternalLinkDemo
    } = props;

    const [isImageLoading, setIsImageLoading] = React.useState(false);

    const history = useHistory();
    const { localize } = useLocalization();
    const projectTypeText = demoType && localize(messages[demoType === 'preview' ? 'previewTag' : 'gaTag']);

    const handleTryItOutLinkClick = () => {
        onDemoClicked?.(id);

        if (disabled) {
            return;
        }

        logDemoCard(cardType, featureName);

        if (isExternalLinkDemo) {
            window.open(demoLink, '_blank');

            return;
        }

        history.push(demoLink);
    };

    const tagToolTipContent = (
        <span>
            {localize(messages.definitionTooltip, { phase: projectTypeText })}
            &nbsp;
            <ExternalLink href={learnMoreLink} styles={tooltipLinkStyles}>
                {localize(globalMessages.learnMore)}
            </ExternalLink>
        </span>
    );

    const tagContent = projectTypeText && <LuisText variant="smallPlus">({projectTypeText})</LuisText>;

    const cardContent = (
        <Stack
            onClick={handleTryItOutLinkClick}
            data-automation-id="demoCardContainer"
            verticalFill={Boolean(tooltipMessage)}
            styles={mergeStyleSets(containerStyles(height, disabled), disabled && disabledContainerStyles)}>
            <Stack grow styles={{ root: { minHeight: '153px' } }} horizontalAlign="center" verticalAlign="center">
                <Image
                    width="100%"
                    alt={demoName}
                    src={imageSrc}
                    shouldStartVisible
                    styles={imageStyles}
                    onLoadingStateChange={loadState => setIsImageLoading(loadState !== ImageLoadState.loaded)}
                />
                {isImageLoading && <Spinner size={SpinnerSize.medium} />}
            </Stack>

            <Stack verticalFill tokens={{ padding: 12, childrenGap: 12 }}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Stack>
                        <LuisText data-automation-id="prebuiltDemoCardDemoName" fontWeight={500}>
                            {demoName}
                        </LuisText>
                        {subtitle && (
                            <LuisText styles={{ root: { fontStyle: 'italic', color: cardTheme.secondaryTextColor, fontSize: '12px' } }}>
                                {subtitle}
                            </LuisText>
                        )}
                    </Stack>

                    {disabled ? (
                        tagContent
                    ) : (
                        <LuisTooltip closeDelay={300} content={tagToolTipContent}>
                            {tagContent}
                        </LuisTooltip>
                    )}
                </Stack>

                <LuisText variant="smallPlus" data-automation-id="prebuiltDemoCardDescription">
                    {description}
                </LuisText>

                <Link disabled={disabled} styles={linkStyles} aria-label={tryOutMessage ?? localize(messages.tryItOut) + demoName}>
                    {tryOutMessage ?? localize(messages.tryItOut)}
                </Link>
            </Stack>
        </Stack>
    );

    return tooltipMessage ? <LuisTooltip content={tooltipMessage}>{cardContent}</LuisTooltip> : cardContent;
};
